import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { yupConstants } from "../../utils/constants/yup";
import { Event } from "../../models/events";
import { useCreateEventMutation } from "../../store/services/events";
import toast from "react-hot-toast";
import { Calendar } from "primereact/calendar";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../utils/constants/calendar-constants";
import { Button } from "primereact/button";
import { useUpdateEventHostMutation } from "../../store/services/events";
import { useGetEventAdvancedConfigVisibilityRulesQuery } from "../../store/services/rules-event-advanced-config";
import { isEventNotificationSuppressionVisible } from "../../utils/eventAdvancedConfigVisibility";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import {
  DUPLICATED_EVENT_ATTENDANCE_FAILED_NOTICE,
  DUPLICATED_EVENT_ATTENDANCE_HOST_RESET_NOTICE,
  DUPLICATED_EVENT_ATTENDANCE_UNKNOWN_NOTICE,
  buildDuplicateEventPayload,
  buildDuplicatedAttendanceConfigPayload,
} from "../../utils/functions/duplicate-event";
import {
  DUPLICATED_EVENT_BONUS_DROPPED_NOTICE,
  resolveDuplicatedEventBonusPoints,
} from "../../utils/functions/event-form";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useGetPointRulesQuery } from "../../store/services/point-rules";
import {
  useGetEventAttendanceConfigQuery,
  useSaveEventAttendanceConfigMutation,
} from "../../store/services/attendances";

interface DuplicateEventFormProps {
  event: Event;
  handleClose: () => void;
}
export const DuplicateEventForm = ({
  event,
  handleClose,
}: DuplicateEventFormProps) => {
  const [createEvent, { isLoading: isLoadingCreate }] =
    useCreateEventMutation();

  const now = new Date();

  const [updateEventHost] = useUpdateEventHostMutation();

  const {
    data: advancedConfigVisibilityRules = [],
    isSuccess: isAdvancedConfigRulesLoaded,
  } = useGetEventAdvancedConfigVisibilityRulesQuery({});

  const pointsFlag = useIsFeatureFlagEnabled("gamification_points_enabled", event.tenantId ?? null);
  const skipPointRules =
    event.bonusPoints == null || !event.tenantId || pointsFlag.loading || !pointsFlag.enabled;
  const {
    currentData: tenantPointRules,
    isUninitialized: isPointRulesUninitialized,
    isFetching: isFetchingPointRules,
  } = useGetPointRulesQuery(event.tenantId ?? "", { skip: skipPointRules });
  // Mientras el flag o las reglas cargan no se sabe si el bonus entra: duplicar en ese
  // momento dejaba la copia sin puntos extra aunque el tenant los permitiera.
  const isBonusPolicyLoading =
    event.bonusPoints != null &&
    (pointsFlag.loading ||
      (!skipPointRules && (isPointRulesUninitialized || isFetchingPointRules)));

  // La asistencia no viaja en el body del evento: hay que leerla del original y
  // mandarla al duplicado en su propio recurso. El flag es por tenant y el GET
  // falla si está apagado, así que se consulta sólo cuando está prendido.
  const attendanceFlag = useIsFeatureFlagEnabled(
    "attendance_manual_enabled",
    event.tenantId ?? null
  );
  const skipAttendanceConfig = attendanceFlag.loading || !attendanceFlag.enabled;
  const {
    currentData: attendanceConfig,
    isUninitialized: isAttendanceConfigUninitialized,
    isFetching: isFetchingAttendanceConfig,
    isError: isAttendanceConfigError,
  } = useGetEventAttendanceConfigQuery(event.id, { skip: skipAttendanceConfig });
  const [saveEventAttendanceConfig] = useSaveEventAttendanceConfigMutation();
  // Duplicar mientras carga dejaría la copia sin asistencia sin avisar nada.
  const isAttendanceConfigLoading =
    attendanceFlag.loading ||
    (!skipAttendanceConfig &&
      !isAttendanceConfigError &&
      (isAttendanceConfigUninitialized || isFetchingAttendanceConfig));

  const { handleSubmit, touched, errors, values, handleBlur, handleChange } =
    useFormik({
      initialValues: {
        startDate: now,
      },
      validationSchema: Yup.object({
        startDate: Yup.date().required(yupConstants().required),
      }),
      onSubmit: async ({ startDate }) => {
        if (isBonusPolicyLoading || isAttendanceConfigLoading) return;
        const duplicatedBonusPoints = resolveDuplicatedEventBonusPoints(
          event.bonusPoints,
          pointsFlag.enabled,
          tenantPointRules
        );
        try {
          const suppressionAllowed =
            !isAdvancedConfigRulesLoaded ||
            isEventNotificationSuppressionVisible(advancedConfigVisibilityRules, {
              tenantId: event.tenantId,
              eventTypeId: event.eventTypeId,
              eventSubTypeId: event.eventSubTypeId,
              isRecurrent: false,
            });

          const response = await createEvent({
            ...buildDuplicateEventPayload(event, startDate, suppressionAllowed),
            bonusPoints: duplicatedBonusPoints,
          }).unwrap();

          // if the event was successfuly created also handle host asignation
          // if the event has a host assigned, assign this host on the duplicate event
          let hostAssigned = false;
          if (response.id && event.hostId) {
            try {
              await updateEventHost({
                id: response.id,
                hostId: event.hostId,
                description: event.hostDescription || "",
                imageFormat: event.hostImageFormat || "",
                imageBase64: event.hostImageBase64 || "",
                hostAssists: event.hostAssists
              }).unwrap();
              hostAssigned = true;
            } catch {
              toast.error("No se pudo asignar el host");
            }
          }

          // Va después del host: el worker rechaza un `attendanceHostUserId` que
          // todavía no esté inscripto como host del evento nuevo.
          const attendancePayload = response.id
            ? buildDuplicatedAttendanceConfigPayload(
                attendanceConfig,
                event,
                response.id,
                hostAssigned
              )
            : null;
          let attendanceHostReset = false;
          let attendanceCopyFailed = false;
          if (attendancePayload) {
            attendanceHostReset =
              attendanceConfig?.attendanceHostUserId != null &&
              attendancePayload.attendanceHostUserId === null;
            try {
              await saveEventAttendanceConfig(attendancePayload).unwrap();
            } catch (error) {
              attendanceCopyFailed = true;
              attendanceHostReset = false;
              const attendanceReason = getApiErrorMessage(error);
              toast.error(
                attendanceReason
                  ? `${DUPLICATED_EVENT_ATTENDANCE_FAILED_NOTICE} ${attendanceReason}`
                  : DUPLICATED_EVENT_ATTENDANCE_FAILED_NOTICE
              );
            }
          }

          toast.success("Evento duplicado exitosamente!");
          if (attendanceHostReset) {
            toast(DUPLICATED_EVENT_ATTENDANCE_HOST_RESET_NOTICE, { icon: "ℹ️" });
          }
          if (!attendanceCopyFailed && isAttendanceConfigError) {
            toast(DUPLICATED_EVENT_ATTENDANCE_UNKNOWN_NOTICE, { icon: "ℹ️" });
          }
          if (event.bonusPoints != null && duplicatedBonusPoints === null) {
            toast(DUPLICATED_EVENT_BONUS_DROPPED_NOTICE, { icon: "ℹ️" });
          }
          handleClose();
        } catch (error) {
          const reason = getApiErrorMessage(error);
          toast.error(reason ? `No se pudo duplicar el evento: ${reason}` : "No se pudo duplicar el evento");
          handleClose();
        }
      },
    });

  return (
    <form className="grid" onSubmit={handleSubmit}>
      <div className="col-12">
        <label htmlFor="startDate">Seleccione nueva fecha</label>
        <span className="p-label">
          <Calendar
            required
            invalid={!!touched.startDate && !!errors.startDate}
            className="w-full"
            id="startDate"
            name="startDate"
            dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
            locale={CALENDAR_LOCALE}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.startDate}
            disabled={isLoadingCreate}
            showTime
            hourFormat="24"
            stepMinute={5}
            minDate={now}
            hideOnDateTimeSelect
            touchUI
          />
        </span>
        {touched.startDate && errors.startDate && (
          <small className="p-error">{yupConstants().required}</small>
        )}
      </div>
      <div className="col-12 flex justify-content-center gap-4">
        <Button
          icon="pi pi-check"
          severity="success"
          type="submit"
          label={"Duplicar"}
          loading={isLoadingCreate}
          disabled={isBonusPolicyLoading || isAttendanceConfigLoading}
        />
        <Button
          icon="pi pi-times"
          severity="secondary"
          type="button"
          label={"Cancelar"}
          onClick={handleClose}
          loading={isLoadingCreate}
        />
      </div>
    </form>
  );
};
